import { apiBase, portalOrigin } from '../config';
import { clearSession, persistSession, readStoredToken, readStoredUser } from './auth-storage';

/** Remove legacy token fragments from the URL without using them. */
export function stripLegacyAuthHash() {
  const raw = window.location.hash.slice(1);
  if (!raw || (!raw.includes('protego_token=') && !raw.includes('protego_user='))) {
    return;
  }

  const kept = raw
    .split('&')
    .filter((part) => part && !part.startsWith('protego_token=') && !part.startsWith('protego_user='));

  const newHash = kept.length ? `#${kept.join('&')}` : '';
  window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}${newHash}`);
}

/** Read one-time handoff code from the query string, then strip it from the URL. */
export function consumeAuthHandoffFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('auth_handoff');
  if (!code) return null;

  params.delete('auth_handoff');
  const newSearch = params.toString();
  window.history.replaceState(
    null,
    '',
    `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}${window.location.hash}`
  );

  return code;
}

export async function exchangeAuthHandoff(code) {
  const returnUrl = `${window.location.origin}${window.location.pathname}${window.location.search}`;

  const res = await fetch(`${apiBase}/api/auth/handoff/exchange`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ code, return_url: returnUrl }),
  });

  if (!res.ok) {
    throw new Error('handoff exchange failed');
  }

  const data = await res.json();
  if (!data?.token) {
    throw new Error('missing token');
  }

  return {
    token: data.token,
    user: data.user || null,
  };
}

export async function fetchCurrentUser(token) {
  const res = await fetch(`${apiBase}/api/auth/user`, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
    },
  });

  if (!res.ok) {
    throw new Error('unauthorized');
  }

  const data = await res.json();
  return data.user || data;
}

export async function logoutOnServer(token) {
  if (!token) return;
  try {
    await fetch(`${apiBase}/api/auth/logout`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    });
  } catch {
    // clear local session even if server logout fails
  }
}

/**
 * Sync token from the portal (app.section3.ai) when this marketing site is on a different origin.
 * Portal serves /auth-bridge.html which postMessages the Sanctum token.
 */
export function syncAuthFromPortalBridge() {
  return new Promise((resolve) => {
    if (readStoredToken()) {
      resolve(null);
      return;
    }

    let settled = false;
    const finish = (result) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', onMessage);
      iframe.remove();
      resolve(result);
    };

    const onMessage = (event) => {
      if (event.origin !== portalOrigin) return;
      if (event.data?.type !== 'protego-auth' || !event.data.token) return;

      let user = null;
      try {
        user = event.data.user ? JSON.parse(event.data.user) : null;
      } catch {
        user = null;
      }

      persistSession(event.data.token, user || readStoredUser() || {});
      finish({ token: event.data.token, user });
    };

    window.addEventListener('message', onMessage);

    const iframe = document.createElement('iframe');
    iframe.src = `${portalOrigin}/auth-bridge.html?parent=${encodeURIComponent(window.location.origin)}`;
    iframe.title = 'Protego auth sync';
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText = 'position:absolute;width:0;height:0;border:0;visibility:hidden';
    document.body.appendChild(iframe);

    window.setTimeout(() => finish(null), 5000);
  });
}

export async function resolveSession() {
  stripLegacyAuthHash();

  const handoffCode = consumeAuthHandoffFromUrl();
  if (handoffCode) {
    try {
      const exchanged = await exchangeAuthHandoff(handoffCode);
      const user = exchanged.user || (await fetchCurrentUser(exchanged.token));
      persistSession(exchanged.token, user);
      return { token: exchanged.token, user };
    } catch (err) {
      console.error('Auth handoff exchange failed', err);
      clearSession();
    }
  }

  const existingToken = readStoredToken();

  if (existingToken) {
    try {
      const user = await fetchCurrentUser(existingToken);
      persistSession(existingToken, user);
      return { token: existingToken, user };
    } catch {
      clearSession();
    }
  }

  const bridged = await syncAuthFromPortalBridge();
  if (!bridged?.token) {
    return { token: null, user: null };
  }

  try {
    const user = await fetchCurrentUser(bridged.token);
    persistSession(bridged.token, user);
    return { token: bridged.token, user };
  } catch {
    clearSession();
    return { token: null, user: null };
  }
}
